import { ExternalLink } from "lucide-react";
import { cn } from "../../app/components/primitives/utils";

/** A term the guide uses, in the dictionary's customer wording. */
export interface SupplyTerm {
  label: string;
  explanation?: string | null;
  /** A full URL only: the read withholds a link that names a guide page. */
  learn_more_url?: string | null;
}

/**
 * What to send for one part of an item: the server's sentence ("32 single
 * pages, 148 × 210 mm, in reading order…") and its short extra points, then
 * each term the guide uses explained in the customer's wording, with its
 * "Learn more" link where the wording has a full URL. Nothing when how to
 * supply the part is not known, so a slot then reads as it always has; no
 * terms while the wording is off. The words are the read's; nothing here
 * assembles them.
 */
export function SupplyGuide({
  sentence,
  notes,
  terms,
  className,
}: {
  sentence?: string | null;
  notes?: string[];
  terms?: SupplyTerm[];
  className?: string;
}) {
  if (!sentence) return null;
  return (
    <div className={cn("space-y-1 text-xs text-muted-foreground", className)}>
      <p>
        <span className="font-medium text-foreground">What to send: </span>
        {sentence}
      </p>
      {notes && notes.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
      {terms && terms.length > 0 && (
        <dl aria-label="What these mean" className="space-y-0.5">
          {terms.map((term) => (
            <div key={term.label}>
              <dt className="inline font-medium text-foreground">
                {term.label}
              </dt>
              {term.explanation && (
                <dd className="inline">: {term.explanation}</dd>
              )}
              {term.learn_more_url && (
                <dd className="inline">
                  {" "}
                  <a
                    href={term.learn_more_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-foreground underline underline-offset-2"
                  >
                    Learn more{" "}
                    <span className="sr-only">
                      about {term.label} (opens in a new tab)
                    </span>
                    <ExternalLink
                      className="inline size-3 align-[-1px]"
                      aria-hidden="true"
                    />
                  </a>
                </dd>
              )}
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
